import { HubConnectionBuilder, LogLevel, type HubConnection } from '@microsoft/signalr';

import { TOKEN_STORAGE_KEY } from '@/api/api';
import { API_URL } from '@/env';

/**
 * AI gateway features. Mirrors AI-Backend's `AiFeature` enum (serialized by name over the wire).
 * Keep in sync with the backend; a value here that the backend doesn't know just won't match.
 */
export type AiFeature =
    | 'Default'
    | 'ReviewSummary'
    | 'ManagerAssessmentSummary'
    | 'MeetingAgenda'
    | 'ObjectiveDraft'
    | 'ThematicMeetingAgenda';

/**
 * Result pushed by the backend when an async AI completion finishes. Matches the server's
 * `AiResultNotification` (camel-cased by SignalR's JSON protocol). Correlate to your request via
 * {@link correlationId}; route on {@link feature}.
 */
export interface AiResultNotification {
    correlationId: string | null;
    feature: AiFeature | null;
    success: boolean;
    content: string | null;
    error: string | null;
    errorCode: string | null;
}

/** The server-to-client method the hub invokes; the client listens for this event name. */
export const AI_RESULT_EVENT = 'ReceiveAiResult';

/** Path the AI hub is mapped at on the performance API (see backend `AiHub.Path`). */
const AI_HUB_PATH = '/hubs/ai';

/** Same token the axios client uses: a dev override, else the host-provided one in localStorage. */
function getAccessToken(): string {
    const devToken = import.meta.env.VITE_DEV_TOKEN;
    const storedToken =
        typeof localStorage === 'undefined' ? null : localStorage.getItem(TOKEN_STORAGE_KEY);
    return devToken || storedToken || '';
}

/** True when a token is available to authenticate the hub (the right signal to open the connection). */
export function hasAiHubToken(): boolean {
    return Boolean(getAccessToken());
}

/**
 * Builds (does not start) the AI hub connection. The JWT is sent as `?access_token=` on the
 * handshake — browsers can't set an Authorization header on a websocket — matching the backend's
 * JwtBearer `OnMessageReceived` wiring. Auto-reconnects on drop.
 */
export function createAiHubConnection(): HubConnection {
    return new HubConnectionBuilder()
        .withUrl(`${API_URL}${AI_HUB_PATH}`, {
            accessTokenFactory: getAccessToken,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
}
